package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.ActorKind
import dev.lingvo.shared.AuditEventDto
import dev.lingvo.shared.RetakeLocaleGroupDto
import dev.lingvo.shared.RetakeReason
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** The public agent surface supplies instructions and prompt metadata without needing an agent credential. */
data class ShotAgentSurface(val instructions: String, val retakeDescription: String?)

/** Reads the existing server surface; it does not fabricate a rendered MCP prompt from metadata. */
suspend fun readShotAgentSurface(): ShotAgentSurface {
    val response = window.fetch("${ApiConfig.baseUrl}/agent-surface").await()
    check(response.ok) { trf("screenshots.agents.surface_failed", "Couldn't load the agent surface ({status}).", "status" to response.status) }
    val body = API_JSON.parseToJsonElement(response.text().await()).jsonObject
    val prompt = body["prompts"]?.jsonArray?.firstOrNull { it.jsonObject["name"]?.jsonPrimitive?.content == "retake_screenshots" }?.jsonObject
    return ShotAgentSurface(body.getValue("instructions").jsonPrimitive.content, prompt?.get("description")?.jsonPrimitive?.content)
}

/** Group labels count the actual queued reasons rather than calling every missing image stale. */
fun shotRetakeGroupLabel(group: RetakeLocaleGroupDto): String = trf("screenshots.agents.group",
    "{language} · {stale} stale · {missing} missing", "language" to dev.lingvo.shared.localeInfo(group.locale).englishName,
    "stale" to group.items.count { it.reason == RetakeReason.STALE }, "missing" to group.items.count { it.reason == RetakeReason.MISSING })

/** Only server-received screenshot events enter the workspace activity section; refusals remain verbatim. */
fun screenshotAgentEvents(events: List<AuditEventDto>): List<AuditEventDto> = events.filter {
    it.action.startsWith("SCREENSHOT_") && (it.actorKind == ActorKind.AGENT || it.payload["source"] == "AGENT")
}.sortedByDescending { it.createdAt }

/** The command never interpolates instructions or titles into a shell. */
fun shotCaptureCommand(): String = "lingvo screenshots capture --command \"./shoot.sh\"" // i18n-ignore

/** Names are documented protocol identifiers; no credential values are displayed or copied. */
val SHOT_CAPTURE_ENVIRONMENT: String = listOf( // i18n-ignore
    "LINGVO_SHOT_LOCALE", "LINGVO_SHOT_POSIX_LOCALE", "LINGVO_SHOT_STORE_LOCALE", "LINGVO_SHOT_DIRECTION",
    "LINGVO_SHOT_SLOT", "LINGVO_SHOT_SLOT_TITLE", "LINGVO_SHOT_POSITION", "LINGVO_SHOT_DEVICE", "LINGVO_SHOT_PLATFORM",
    "LINGVO_SHOT_OUT", "LINGVO_SHOT_WIDTH", "LINGVO_SHOT_HEIGHT", "LINGVO_SHOT_SIZES", "LINGVO_SHOT_ALPHA",
    "LINGVO_SHOT_HOWTO", "LINGVO_SHOT_DEMO", "LINGVO_SHOT_REASON", "LINGVO_SHOT_ATTEMPT", "LINGVO_SHOT_PREVIOUS_FAILURE",
    "LINGVO_SHOT_PREVIOUS_SIZE", "LINGVO_SHOT_LOCALE_CHANGED", "LINGVO_SHOT_WORKER", "LINGVO_SHOT_INDEX",
    "LINGVO_SHOT_TOTAL", "LINGVO_SHOT_RUN_DIR", "LINGVO_SHOT_DEVICES", "LINGVO_SHOT_LOCALES",
).joinToString("\n")

/** Empty-queue copy uses the actual language count and its locale's plural category. */
fun shotRetakeEmptyDescription(count: Int): String = tPlural("screenshots.agents.empty_description", count,
    "Every capture is current in {count} language.", "Every capture is current in all {count} languages.")
